import React from 'react';

import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  BorderColor as BorderColorIcon,
  Delete as DeleteFilledIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { Typography, useTheme } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber } from '@clearverify/utils';

import {
  UserStatus,
  useResendInviteToVerifierStaffMutation,
  type VerifierStaffItemFragment,
} from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import type { TableColumn, TableFilter } from 'src/components/table';
import { Table, TableRowAction, TableThemes } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { BusinessRole } from 'src/constants/user';
import { useNotification } from 'src/hooks/app';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { MfaManagementModal } from 'src/modals/auth';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { DeleteVerifierStaffConfirmationModal } from 'src/modals/verifierStaff';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { hasOneOfRoles } from 'src/utils/role';
import { getUserFullName } from 'src/utils/user';

type VerifierStaffTableClearVerifyAdminProps = {
  verifierStaffList: VerifierStaffItemFragment[];
  verifierStaffCount: number;
  loading: boolean;
  onRefresh?: () => Promise<void>;
  pagination: PaginationContextValue;
};

export const VerifierStaffTableClearVerifyAdmin: React.FC<
  VerifierStaffTableClearVerifyAdminProps
> = ({ verifierStaffList, verifierStaffCount, loading, onRefresh, pagination }) => {
  const theme = useTheme();

  const { setSearchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();

  const { createApolloContext } = useNotification();
  const { isPlatformAdmin } = useCurrentUserPermissions();

  const [resendInviteToVerifierStaff] = useResendInviteToVerifierStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  const rowActions = React.useMemo(
    (): Array<TableRowAction<VerifierStaffItemFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: staff => {
          openModal(ModalId.VerifierStaffDetailsModal, {
            verifierStaffId: staff.id,
            isAdmin: isPlatformAdmin,
          });
        },
      },
      {
        startIcon: <AdminPanelSettingsIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'MFA Management',
        onAction: staff => {
          openModalV2(MfaManagementModal, { id: ModalId.MfaManagement, userId: staff.user.id });
        },
      },
      {
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'Verifier',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToVerifierStaff({
                variables: { verifierStaffId: staff.id },
              });
              return data?.resendInviteToVerifierStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      },
      ...(isPlatformAdmin
        ? [
            {
              startIcon: <DeleteFilledIcon sx={{ margin: '0 10px 0 0', color: 'error.main' }} />,
              actionName: 'Delete Staff',
              style: { color: theme.palette.error.main },
              onAction: (staff: VerifierStaffItemFragment) => {
                openModalV2(DeleteVerifierStaffConfirmationModal, {
                  id: ModalId.DeleteVerifierStaffConfirmationModal,
                  verifierStaffId: staff.id,
                });
              },
            },
          ]
        : []),
    ],
    [
      isPlatformAdmin,
      openModal,
      openModalV2,
      resendInviteToVerifierStaff,
      theme.palette.error.main,
    ],
  );

  return (
    <Table
      dataSource={verifierStaffList}
      rowCount={verifierStaffCount}
      columns={columns}
      rowActions={rowActions}
      onSearch={setSearchValue}
      onSort={setSortOption}
      pagination={pagination}
      filters={filters}
      loading={loading}
      tableTheme={TableThemes.withBorderWithoutProfileIcon}
      onRefresh={onRefresh}
    />
  );
};

const columns: Array<TableColumn<VerifierStaffItemFragment>> = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, verifierStaff, { rowActions }) => {
      const isVerifierAdmin = hasOneOfRoles(verifierStaff, [BusinessRole.VerifierAdmin]);
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(verifierStaff)}
          label={getUserFullName(verifierStaff.user)}
          labelPostfix={isVerifierAdmin ? 'Admin' : undefined}
          withoutLogo
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, verifierStaff) => {
      return (
        <Typography variant="body2">
          <DemoPrivacyText>
            {formatPhoneNumber(verifierStaff.user.phoneNumber) || '-'}
          </DemoPrivacyText>
        </Typography>
      );
    },
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verifierStaff) => {
      return (
        <Typography variant="body2">
          <DemoPrivacyText>{verifierStaff.user.email || '-'}</DemoPrivacyText>
        </Typography>
      );
    },
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, verifierStaff) => {
      return <UserStatusChip userStatus={verifierStaff.user.status} />;
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
